import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import {
  CalendarIcon,
  EllipsisVerticalIcon,
  MapPinIcon,
} from "@heroicons/react/20/solid";
import StatusIndicator from "../Shared/StatusIndicator";
import { APPOINTMENT_STATUS } from "../../config/appointment";
import { DateTime } from "luxon";
import { useUserPreferences } from "../../hooks/useUserPreferences";
import { getOriginalTimeDisplay } from "../../utility/timezoneUtils";

const AppointmentList = ({
  appointments,
  onView,
  onEdit,
  onCancel,
  onClientClick,
}) => {
  const systemTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { is12HourClock } = useUserPreferences();

  const formatAppointmentTime = (time) => {
    if (!time) return "";
    const format = is12HourClock
      ? "MMMM d, yyyy, h:mm a"
      : "MMMM d, yyyy, HH:mm";
    return DateTime.fromISO(time).setZone(systemTimezone).toFormat(format);
  };

  const formatAppointment = (appointment) => {
    const status = APPOINTMENT_STATUS.find((x) => x.id === appointment.status);
    return {
      ...appointment,
      id: appointment.id,
      startTime: formatAppointmentTime(appointment.startTime),
      endTime: formatAppointmentTime(appointment.endTime),
      status: status.name,
      statusColour: status.colour,
      address: appointment.serviceAddress.full,
    };
  };

  const getOriginalTimeDisplayForAppointment = (appointment) => {
    if (
      !appointment.timeZoneId ||
      appointment.timeZoneId === systemTimezone ||
      !appointment.startTime
    ) {
      return null;
    }

    // For this component, we need to pass the ISO string directly
    // since the appointment.startTime is the raw ISO string from the API
    return getOriginalTimeDisplay(
      appointment.startTime,
      appointment.timeZoneId,
      is12HourClock
    );
  };

  return (
    <ol className="mt-4 divide-y divide-ink-100 text-sm/6 lg:col-span-7 xl:col-span-8">
      {appointments.map((appointment) => {
        const formattedAppointment = formatAppointment(appointment);
        const originalTimeDisplay =
          getOriginalTimeDisplayForAppointment(appointment);
        const showCancelOption =
          appointment.status !== "cancelled" &&
          appointment.status !== "completed";

        return (
          <li
            key={appointment.id}
            className="relative flex gap-x-6 py-6 xl:static"
          >
            <div className="flex-auto min-w-0">
              <div className="flex items-start gap-x-3">
                <button
                  type="button"
                  onClick={() => onClientClick?.(appointment.clientId)}
                  disabled={!onClientClick || !appointment.clientId}
                  className={`font-semibold text-ink-900 xl:pr-0 truncate text-left transition-colors duration-150 ${
                    onClientClick && appointment.clientId
                      ? "hover:text-accent-500 hover:underline"
                      : "disabled:cursor-default"
                  }`}
                >
                  {appointment.clientFullName}
                </button>
                <StatusIndicator
                  colour={formattedAppointment.statusColour}
                  label={formattedAppointment.status}
                />
              </div>
              <dl className="mt-2 flex flex-col text-ink-500 xl:flex-row">
                <div className="flex items-start gap-x-3 flex-shrink-0">
                  <dt className="mt-0.5 flex-shrink-0">
                    <span className="sr-only">Date</span>
                    <CalendarIcon
                      className="size-5 text-ink-400"
                      aria-hidden="true"
                    />
                  </dt>
                  <dd className="whitespace-nowrap">
                    <time dateTime={appointment.startTime} className="block">
                      {formattedAppointment.startTime}
                    </time>
                    {originalTimeDisplay && (
                      <p className="text-xs text-ink-500 mt-0.5">
                        {originalTimeDisplay}
                      </p>
                    )}
                  </dd>
                </div>
                <div className="mt-2 flex items-start gap-x-3 xl:ml-3.5 xl:mt-0 xl:border-l xl:border-ink-300/50 xl:pl-3.5 flex-1 min-w-0">
                  <dt className="mt-0.5 flex-shrink-0">
                    <span className="sr-only">Location</span>
                    <MapPinIcon
                      className="size-5 text-ink-400"
                      aria-hidden="true"
                    />
                  </dt>
                  <dd className="truncate">
                    {appointment.serviceAddress.full}
                  </dd>
                </div>
              </dl>
            </div>
            <Menu
              as="div"
              className="absolute right-0 top-6 xl:relative xl:right-auto xl:top-auto xl:self-center flex-shrink-0"
            >
              <div>
                <MenuButton className="-m-2 flex items-center rounded-full p-2 text-ink-500 hover:text-ink-600">
                  <span className="sr-only">Open options</span>
                  <EllipsisVerticalIcon
                    className="size-5"
                    aria-hidden="true"
                  />
                </MenuButton>
              </div>

              <MenuItems
                anchor="bottom end"
                transition
                className="z-10 w-36 rounded-md bg-white shadow-lg ring-1 ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
              >
                <div className="py-1">
                  <MenuItem>
                    {({ close }) => (
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          onView({
                            ...formattedAppointment,
                            clientName: appointment.clientFullName,
                          });
                          close();
                        }}
                        className="block px-4 py-2 text-sm text-ink-700 data-[focus]:bg-cream-200 data-[focus]:text-ink-900 data-[focus]:outline-none"
                      >
                        View
                      </a>
                    )}
                  </MenuItem>
                  <MenuItem>
                    {({ close }) => (
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          onEdit(appointment);
                          close();
                        }}
                        className="block px-4 py-2 text-sm text-ink-700 data-[focus]:bg-cream-200 data-[focus]:text-ink-900 data-[focus]:outline-none"
                      >
                        Edit
                      </a>
                    )}
                  </MenuItem>
                  {showCancelOption && (
                    <MenuItem>
                      {({ close }) => (
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            onCancel(appointment);
                            close();
                          }}
                          className="block px-4 py-2 text-sm text-signal-red-600 data-[focus]:bg-cream-200 data-[focus]:text-red-900 data-[focus]:outline-none"
                        >
                          Cancel
                        </a>
                      )}
                    </MenuItem>
                  )}
                </div>
              </MenuItems>
            </Menu>
          </li>
        );
      })}
    </ol>
  );
};

export default AppointmentList;
